/**
 * Shared strict email validation — single source of truth for every email
 * input in the app (client forms and server actions).
 *
 * Validates three layers:
 *  1. Format  — RFC-ish shape check.
 *  2. TLD     — the domain ending must be a recognized TLD (rejects .con/.cmo/...).
 *  3. Domain  — near-miss typos of popular providers (gmial.com, gmail.co)
 *               are rejected with a "did you mean" suggestion.
 *
 * Typo-detection logic (sift3 distance + domain suggestion) adapted from
 * @zootools/email-spell-checker (MIT):
 * https://github.com/smashsend/email-spell-checker
 */

export interface EmailValidationResult {
  valid: boolean;
  /** Trimmed + lowercased input, safe to persist when valid. */
  normalized: string;
  /** Human-readable message when invalid. */
  error?: string;
  /** Suggested corrected address (e.g. "john@gmail.com") when a likely typo was detected. */
  suggestion?: string;
}

const EMAIL_FORMAT = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Domains that are always accepted verbatim (and used as typo-suggestion
 * targets). Based on the email-spell-checker default list, extended with
 * providers relevant to our customer base.
 */
const KNOWN_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "yahoo.ca",
  "yahoo.fr",
  "hotmail.com",
  "hotmail.co.uk",
  "hotmail.fr",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "aim.com",
  "comcast.net",
  "att.net",
  "verizon.net",
  "sbcglobal.net",
  "bellsouth.net",
  "cox.net",
  "charter.net",
  "earthlink.net",
  "optonline.net",
  "optusnet.com.au",
  "rocketmail.com",
  "ymail.com",
  "mail.com",
  "gmx.com",
  "gmx.net",
  "web.de",
  "qq.com",
  "sky.com",
  "btinternet.com",
  "telus.net",
  "sympatico.ca",
  "rogers.com",
  "shaw.ca",
  "xtra.co.nz",
  "proton.me",
  "pm.me",
  "protonmail.com",
  "protonmail.ch",
  "fastmail.com",
  "hey.com",
  "zoho.com",
  "yandex.com",
  "titan.email",
  "email.com",
  "y7mail.com",
  "chartermi.net",
  "stateandliberty.com",
];

// QA/test conventions used across the team (see is_test-stamped rows).
// Accepted verbatim, but kept OUT of the typo-suggestion targets — a real
// domain must never be "corrected" to a fake one (rocket.com ≠ mocked.com).
const TEST_DOMAINS = ["test.com", "example.com", "example.org", "mocked.com"];

/**
 * Second-level names with many legitimate country variants (yahoo.co.uk,
 * hotmail.fr, ...). When the second-level name and the TLD are both known,
 * no typo suggestion is made. Deliberately excludes gmail/icloud — those
 * have a single real domain, so gmail.co IS a typo.
 */
const KNOWN_SECOND_LEVEL = ["yahoo", "hotmail", "mail", "live", "outlook"];

/** Recognized TLDs (single- and two-label endings both checked against this list). */
const KNOWN_TLDS = [
  "com", "net", "org", "edu", "gov", "mil", "info", "biz", "co",
  // country codes
  "ae", "ar", "at", "au", "be", "bg", "br", "ca", "cf", "ch", "cl", "cn",
  "cz", "de", "dk", "ee", "es", "eu", "fi", "fr", "ga", "gq", "gr", "hk",
  "hr", "hu", "id", "ie", "il", "in", "ir", "is", "it", "jp", "kr", "lt",
  "lv", "ml", "mx", "my", "nl", "no", "nu", "nz", "ph", "pk", "pl", "pt",
  "ro", "rs", "ru", "sa", "se", "sg", "si", "sk", "so", "th", "tk", "to",
  "tr", "tv", "tw", "ua", "uk", "us", "vg", "vn", "ws", "za",
  // compound country endings
  "com.au", "com.tw", "co.nz", "co.uk", "co.il", "co.jp", "net.au",
  // modern gTLDs
  "app", "ai", "blog", "cc", "cloud", "club", "dev", "digital", "email",
  "finance", "io", "me", "mobi", "network", "pw", "software", "tech",
  "website", "xyz",
  "church", "pro", "inc", "llc", "ltd", "life", "live", "online", "site",
  "store", "shop", "studio", "group", "world", "agency", "media", "law",
  "health", "homes", "events", "wedding", "photography", "global",
  "miami", "nyc", "vegas", "boston",
];

const KNOWN_DOMAIN_SET = new Set([...KNOWN_DOMAINS, ...TEST_DOMAINS]);
const KNOWN_TLD_SET = new Set(KNOWN_TLDS);

/** Frequent mistypings of ".com" — always corrected to "com". */
const COM_TYPO_TLDS = new Set([
  "con", "cmo", "ocm", "cpm", "vom", "xom", "coj", "cok", "coo", "cco", "cp",
]);

// Fuzzy typo detection only runs against long, distinctive provider domains
// (gmail.com and up). Short domains match far too much of the real world:
// td.com, rbc.com, ml.com, key.com all sit within edit distance 1–2 of
// me.com/mac.com/hey.com. Short providers still pass via the exact-match
// allowlist; their typos just aren't suggested.
const FUZZY_DOMAIN_TARGETS = KNOWN_DOMAINS.filter((d) => d.length >= 9);
const DOMAIN_TYPO_THRESHOLD = 2;
const TLD_TYPO_THRESHOLD = 1;

/** Sift3 string distance — fast approximation of edit distance. */
function sift3Distance(s1: string, s2: string): number {
  if (!s1) return s2 ? s2.length : 0;
  if (!s2) return s1.length;

  let c = 0;
  let offset1 = 0;
  let offset2 = 0;
  let lcs = 0;
  const maxOffset = 5;

  while (c + offset1 < s1.length && c + offset2 < s2.length) {
    if (s1.charAt(c + offset1) === s2.charAt(c + offset2)) {
      lcs++;
    } else {
      offset1 = 0;
      offset2 = 0;
      for (let i = 0; i < maxOffset; i++) {
        if (c + i < s1.length && s1.charAt(c + i) === s2.charAt(c)) {
          offset1 = i;
          break;
        }
        if (c + i < s2.length && s1.charAt(c) === s2.charAt(c + i)) {
          offset2 = i;
          break;
        }
      }
    }
    c++;
  }
  return (s1.length + s2.length) / 2 - lcs;
}

function findClosestDomain(
  domain: string,
  domains: string[],
  threshold: number,
): string | undefined {
  let minDist = Infinity;
  let closest: string | undefined;

  for (const candidate of domains) {
    if (domain === candidate) return domain;
    const dist = sift3Distance(domain, candidate);
    if (dist < minDist) {
      minDist = dist;
      closest = candidate;
    }
  }
  return minDist <= threshold ? closest : undefined;
}

interface EmailParts {
  address: string;
  domain: string;
  secondLevelDomain: string;
  topLevelDomain: string;
}

function parseEmail(email: string): EmailParts | null {
  const parts = email.split("@");
  if (parts.length < 2 || parts.some((p) => p === "")) return null;

  const domain = parts.pop() as string;
  const labels = domain.split(".");
  if (labels.some((l) => l === "")) return null;

  let sld = "";
  let tld = "";
  if (labels.length === 1) {
    tld = labels[0];
  } else {
    sld = labels[0];
    tld = labels.slice(1).join(".");
  }

  return {
    address: parts.join("@"),
    domain,
    secondLevelDomain: sld,
    topLevelDomain: tld,
  };
}

/** True when the domain's ending (last one or two labels) is a recognized TLD. */
function hasKnownTld(domain: string): boolean {
  const labels = domain.split(".");
  if (labels.length < 2) return false;
  const lastOne = labels[labels.length - 1];
  const lastTwo = labels.slice(-2).join(".");
  return KNOWN_TLD_SET.has(lastOne) || KNOWN_TLD_SET.has(lastTwo);
}

/** Suggest a corrected domain for a likely typo, or undefined when none. */
function suggestDomain(email: EmailParts): string | undefined {
  // Known second-level + known TLD (e.g. yahoo.co.uk) — never suggest.
  if (
    KNOWN_SECOND_LEVEL.includes(email.secondLevelDomain) &&
    KNOWN_TLD_SET.has(email.topLevelDomain)
  ) {
    return undefined;
  }

  const closestDomain = findClosestDomain(
    email.domain,
    FUZZY_DOMAIN_TARGETS,
    DOMAIN_TYPO_THRESHOLD,
  );
  if (closestDomain) {
    // Exact match is handled by the caller; a close match is a typo.
    return closestDomain === email.domain ? undefined : closestDomain;
  }

  // TLD-only correction (company.con → company.com). No fuzzy second-level
  // correction: it misfired on real domains (maine.edu → "mail.edu").
  const closestTld = COM_TYPO_TLDS.has(email.topLevelDomain)
    ? "com"
    : findClosestDomain(email.topLevelDomain, KNOWN_TLDS, TLD_TYPO_THRESHOLD);

  if (
    closestTld &&
    closestTld !== email.topLevelDomain &&
    email.secondLevelDomain !== ""
  ) {
    return email.domain.replace(
      new RegExp(`${email.topLevelDomain.replace(/\./g, "\\.")}$`),
      closestTld,
    );
  }

  return undefined;
}

/**
 * Strict validation for emails entering the system (intake forms, admin
 * forms, server actions). Rejects malformed addresses, unrecognized TLDs,
 * and near-miss typos of popular providers.
 */
export function validateEmail(raw: string): EmailValidationResult {
  const normalized = raw.trim().toLowerCase();

  if (!normalized || !EMAIL_FORMAT.test(normalized)) {
    return {
      valid: false,
      normalized,
      error: "Enter a valid email address.",
    };
  }

  const parsed = parseEmail(normalized);
  if (!parsed) {
    return {
      valid: false,
      normalized,
      error: "Enter a valid email address.",
    };
  }

  // Exact match against a known provider — always accepted.
  if (KNOWN_DOMAIN_SET.has(parsed.domain)) {
    return { valid: true, normalized };
  }

  const domainSuggestion = suggestDomain(parsed);
  const suggestion = domainSuggestion
    ? `${parsed.address}@${domainSuggestion}`
    : undefined;

  if (!hasKnownTld(parsed.domain)) {
    return {
      valid: false,
      normalized,
      error: suggestion
        ? `Did you mean ${suggestion}?`
        : `"${parsed.domain}" doesn't look like a valid email domain.`,
      suggestion,
    };
  }

  if (suggestion) {
    return {
      valid: false,
      normalized,
      error: `Did you mean ${suggestion}?`,
      suggestion,
    };
  }

  return { valid: true, normalized };
}

/** Convenience boolean wrapper around {@link validateEmail}. */
export function isValidEmail(raw: string): boolean {
  return validateEmail(raw).valid;
}

/**
 * Format-only check for flows that reference EXISTING accounts (login,
 * magic link, password reset). Deliberately not strict: an already
 * registered address must never be blocked from signing in.
 */
export function isEmailFormatValid(raw: string): boolean {
  return EMAIL_FORMAT.test(raw.trim().toLowerCase());
}
